import React, { useState, useEffect, useRef } from "react";
import Modal from "react-modal";
import { Document, Page, pdfjs } from "react-pdf";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// Configure pdfjs worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    transform: "translate(-50%, -50%)",
    // maxHeight: "80vh", // Adjust the height as needed
    overflowY: "auto",
  },
  page: {
    marginBottom: "20px", // Adjust as needed
    borderBottom: "1px solid #ccc", // Border between pages
  },
};

const PDFViewer = ({ url, isOpen, onClose, filename, pageNumber }) => {
  const [numPages, setNumPages] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [targetPage, setTargetPage] = useState(1); // The page to scroll to (1-based index)
  const pageRefs = useRef([]); // Array to store refs for pages
  const targetPageRendered = useRef(false); // Track rendering of the target page
  const [markdownContent, setMarkdownContent] = useState("");

  useEffect(() => {
    Modal.setAppElement("body"); // Set the app element to body
  }, []);

  useEffect(() => {
    if (pageNumber) {
      setTargetPage(pageNumber);
    }
  }, [pageNumber]);

  useEffect(() => {
    if (isOpen) {
      setIsLoaded(false);
      setError(null);
      targetPageRendered.current = false;
      if (filename.endsWith(".md")) {
        fetch(url)
          .then((response) => response.text())
          .then((text) => {
            setMarkdownContent(text);
            setIsLoaded(true);
          })
          .catch((err) => setError(err));
      }
    }
  }, [isOpen, url, filename]);

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
    setIsLoaded(true);
  }

  function onPageRenderSuccess(pageNumber) {
    // Check if the target page is rendered
    if (pageNumber === targetPage) {
      targetPageRendered.current = true;
      // Scroll to the target page
      if (pageRefs.current[pageNumber - 1]) {
        pageRefs.current[pageNumber - 1].scrollIntoView({ behavior: "smooth" });
      }
    }
  }

  function onError(error) {
    setError(error);
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={customStyles}
      contentLabel="PDF Viewer Modal"
      appElement={document.getElementById("root")} // Set the app element
    >
      <div style={{ position: "relative" }}>
        <div className="PDF-viewer-header-outer pdf-viewer-header-sticky">
          {isLoaded && <h2 className="pdf-viewer-title">{filename}</h2>}
          {isLoaded && (
            <button
              onClick={onClose}
              className="pdf-viewer-close-btn"
              data-cy="doc-preview-close"
            >
              <img src="/images/Remove.svg" alt="Close" />
            </button>
          )}
          {error && <div>Error: {error.message}</div>}
        </div>
        {filename.endsWith(".md") ? (
          <div style={{ padding: "20px" }}>
            {error ? (
              <div style={{ position: "absolute", top: "10px", left: "10px", fontSize: "17px" }}>Error: Invalid MD structure. Failed to load MD file.</div>
            ) : !isLoaded ? (
              <div style={{ position: "absolute", top: "10px", left: "10px", fontSize: "17px" }}>Loading Markdown...</div>
            ) : (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
              >
                {markdownContent}
              </ReactMarkdown>
            )}
          </div>
        ) : (
          <Document
            file={url}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onError}
          >
            {Array.from(new Array(numPages), (el, index) => (
              <div
                key={`page_${index + 1}`}
                style={
                  index < numPages - 1 ? customStyles.page : { marginBottom: "0" }
                }
                ref={(el) => (pageRefs.current[index] = el)} // Assign ref for each page
              >
                <Page
                  pageNumber={index + 1}
                  onRenderSuccess={() => onPageRenderSuccess(index + 1)} // Track rendering
                />
              </div>
            ))}
          </Document>
        )}
      </div>
    </Modal>
  );
};

export default PDFViewer;